import { useEffect, useRef, useState } from 'react'
import type { Capacity } from '../domain/dailyEngine'
import { CAPACITY_MODES } from '../domain/capacityMode'

interface JourneyModeControlProps {
  value: Capacity
  onChange: (value: Capacity) => void
}

export function JourneyModeControl({ value, onChange }: JourneyModeControlProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const selected = CAPACITY_MODES[value]

  useEffect(() => {
    function closeOnOutsidePress(event: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', closeOnOutsidePress)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePress)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [])

  return <div className="journey-mode-dock" ref={rootRef}>
    {open && <section className="journey-mode-panel" id="journey-mode-panel" aria-labelledby="journey-mode-title">
      <header>
        <div><p className="eyebrow">Select your level for today</p><h2 id="journey-mode-title">What can you honestly carry?</h2></div>
        <button type="button" className="journey-mode-close" onClick={() => setOpen(false)} aria-label="Close today’s mode selector">×</button>
      </header>
      <div className="journey-mode-options" role="radiogroup" aria-label="Today’s mode">
        {(Object.entries(CAPACITY_MODES) as Array<[Capacity, typeof selected]>).map(([mode, copy]) => <button
          type="button"
          role="radio"
          aria-checked={value === mode}
          aria-label={`Choose ${copy.name}`}
          className={value === mode ? 'is-active' : ''}
          key={mode}
          onClick={() => { onChange(mode); setOpen(false) }}
        >
          <span className="journey-mode-option-mark" aria-hidden="true">{copy.mark}</span>
          <span><strong>{copy.name}</strong><small>{copy.selectorDetail}</small></span>
        </button>)}
      </div>
      <p className="journey-mode-boundary">One choice adjusts the demand across The Primal Foundation and Your Primal Path. It changes what is useful today—never your direction, progress or worth.</p>
    </section>}

    <button
      type="button"
      className="journey-mode-trigger"
      aria-expanded={open}
      aria-controls="journey-mode-panel"
      aria-label={`Your mode: ${selected.name}. Change today’s mode`}
      onClick={() => setOpen((current) => !current)}
    >
      <span className="journey-mode-trigger-mark" aria-hidden="true">{selected.mark}</span>
      <span><small>Your mode</small><strong>{selected.name}</strong></span>
    </button>
  </div>
}
